import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../Common/Layout/Header";
import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiArrowLeftRightLine,
  RiMailLine,
  RiWhatsappLine,
  RiInformationLine,
  RiAlertLine,
  RiRefundLine,
  RiShieldCheckLine,
  RiLockLine,
  RiArrowLeftLine,
  RiExternalLinkLine,
  RiTimerLine,
  RiCloseCircleLine,
  RiRefreshLine,
  RiArrowRightLine,
} from "react-icons/ri";

const BlockedSeatsConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    bus,
    searchQuery,
    selectedSeats,
    passengerCount,
    passengerDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    totalAmount,
    baseFare,
    convenienceFee,
    bookingStatus = "BLOCKED",
    bookingId,
    bookingNo,
    invoiceNo,
    bookTicketKey,
    blockTicketKey,
    message = "",
    errorMessage,
    errorCode,
    blockExpiryTime,
    apiResponse,
    apiStatus,
  } = location.state || {};

  const isBlocked = bookingStatus === "BLOCKED";
  const isFailed = bookingStatus === "BLOCK_FAILED";
  const hasError = isFailed || errorMessage;

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `₹${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", options);
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Calculate expiry time (2 hours from now)
  const calculateExpiryTime = () => {
    const now = new Date();
    const expiry = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours
    return expiry.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate minutes remaining
  const calculateMinutesRemaining = () => {
    const now = new Date();
    const expiry = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    const diffMs = expiry - now;
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins;
  };

  const handleCompletePayment = () => {
    navigate("/bus/complete-payment", {
      state: {
        ...location.state,
        // Include all necessary data for payment
      },
    });
  };

  const handleRetryBlocking = () => {
    // Navigate back to payment page to try again
    navigate("/bus/payment", {
      state: {
        bus,
        searchQuery,
        selectedSeats,
        passengerCount,
        passengerDetails,
        selectedBoardingPoint,
        selectedDroppingPoint,
        totalAmount,
        baseFare,
        convenienceFee,
      },
    });
  };

  const handleViewBlockedBookings = () => {
    navigate("/bus/blocked-bookings");
  };

  const handleNewBooking = () => {
    navigate("/bus/search");
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const printContent = document.getElementById("blocked-details").innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleContactSupport = () => {
    const supportPhone = "+91-XXXXXXXXXX";
    const supportEmail = "support@example.com";

    const message = `Blocked Seats Status: ${bookingStatus}\nReference: ${
      bookingNo || "N/A"
    }\nError: ${errorMessage || "N/A"}`;

    const whatsappUrl = `https://wa.me/${supportPhone.replace(
      /\D/g,
      ""
    )}?text=${encodeURIComponent(message)}`;
    const mailtoUrl = `mailto:${supportEmail}?subject=Blocked Seats Issue - ${
      bookingNo || "Error"
    }&body=${encodeURIComponent(message)}`;

    const shouldCall = window.confirm(
      `Contact Support:\n\nPhone: ${supportPhone}\nEmail: ${supportEmail}\n\nClick OK to call, Cancel to email.`
    );

    if (shouldCall) {
      window.location.href = `tel:${supportPhone}`;
    } else {
      window.location.href = mailtoUrl;
    }
  };

  const minutesRemaining = calculateMinutesRemaining();
  const expiryTime = calculateExpiryTime();

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Action Buttons - Top */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <button
                      className="btn btn-outline-primary d-flex align-items-center gap-2"
                      onClick={() => navigate("/bus/search")}
                    >
                      <RiArrowLeftLine />
                      Back to Search
                    </button>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {!hasError && (
                      <>
                        <button
                          className="btn btn-secondary d-flex align-items-center gap-2"
                          onClick={handlePrint}
                        >
                          <RiPrinterLine /> Print
                        </button>
                        <button
                          className="btn btn-success d-flex align-items-center gap-2"
                          onClick={handleDownload}
                        >
                          <RiDownloadLine /> Download
                        </button>
                      </>
                    )}

                    {isBlocked && (
                      <button
                        className="btn btn-info d-flex align-items-center gap-2"
                        onClick={handleViewBlockedBookings}
                      >
                        View Blocked Bookings
                      </button>
                    )}

                    <button
                      className="btn btn-primary d-flex align-items-center gap-2"
                      onClick={handleNewBooking}
                    >
                      <RiBusLine /> New Booking
                    </button>

                    {hasError && (
                      <button
                        className="btn btn-warning d-flex align-items-center gap-2"
                        onClick={handleRetryBlocking}
                      >
                        <RiRefreshLine /> Try Again
                      </button>
                    )}

                    <button
                      className="btn btn-warning d-flex align-items-center gap-2"
                      onClick={handleContactSupport}
                    >
                      <RiInformationLine /> Support
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div id="blocked-details">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="row">
                    {/* Status Banner */}
                    <div className="col-12 mb-4">
                      <div
                        className={`alert ${
                          isBlocked ? "alert-warning" : "alert-danger"
                        } border-0 shadow`}
                      >
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center gap-3">
                              <div className="status-icon-container"></div>
                              <div className="flex-grow-1">
                                <h4
                                  className={`mb-1 ${
                                    isBlocked ? "text-warning" : "text-danger"
                                  }`}
                                >
                                  {isBlocked
                                    ? "Seats Blocked Successfully!"
                                    : "Failed to Block Seats"}
                                </h4>
                                <p className="mb-2">
                                  {message ||
                                    (isBlocked
                                      ? "Your seats have been reserved. Complete payment to confirm booking."
                                      : "There was an issue blocking your seats.")}
                                </p>

                                {/* Success - Time Warning */}
                                {isBlocked && (
                                  <div className="time-warning mt-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <RiTimerLine className="text-danger fs-4" />
                                      <div>
                                        <strong className="text-danger">
                                          Time Sensitive: {minutesRemaining}{" "}
                                          minutes remaining
                                        </strong>
                                        <div className="small">
                                          Complete payment before {expiryTime}{" "}
                                          to confirm your seats
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Error Details */}
                                {hasError && (
                                  <div className="error-details mt-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <RiErrorWarningLine className="text-danger fs-4" />
                                      <div>
                                        <strong className="text-danger">
                                          Error Details:
                                        </strong>
                                        <div className="small">
                                          {errorMessage ||
                                            "Unknown error occurred"}
                                        </div>
                                        {errorCode && (
                                          <div className="small text-muted">
                                            Error Code: {errorCode}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="col-md-4 text-md-end mt-3 mt-md-0">
                            <div className="d-flex flex-column gap-2">
                              {isBlocked && (
                                <>
                                  <div className="alert alert-light mb-0">
                                    <small className="text-muted">
                                      <RiShieldCheckLine className="me-1" />
                                      Your seats are secured temporarily
                                    </small>
                                  </div>
                                  <button
                                    className="btn btn-success"
                                    onClick={handleCompletePayment}
                                  >
                                    <RiCheckboxCircleLine className="me-2" />
                                    Complete Payment Now
                                  </button>
                                </>
                              )}

                              {hasError && (
                                <>
                                  <div className="alert alert-light mb-0">
                                    <small className="text-muted">
                                      Blocking failed. Please try again.
                                    </small>
                                  </div>
                                  <button
                                    className="btn btn-primary"
                                    onClick={handleRetryBlocking}
                                  >
                                    <RiRefreshLine className="me-2" />
                                    Try Blocking Again
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Booking Reference Card - Only show if we have reference details */}
                    {(bookingNo || bookTicketKey || invoiceNo) && (
                      <div className="col-12 mb-4">
                        <div
                          className={`card ${
                            isBlocked ? "border-warning" : "border-danger"
                          }`}
                        >
                          <div
                            className={`card-header ${
                              isBlocked ? "bg-warning" : "bg-danger"
                            } text-white`}
                          >
                            <h5 className="mb-0">Booking Reference</h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {bookingNo && (
                                <div className="col-md-3">
                                  <div className="reference-item">
                                    <small className="text-muted d-block">
                                      Booking Number
                                    </small>
                                    <strong>{bookingNo}</strong>
                                  </div>
                                </div>
                              )}
                              {(bookTicketKey || blockTicketKey) && (
                                <div className="col-md-3">
                                  <div className="reference-item">
                                    <small className="text-muted d-block">
                                      Block Ticket Key
                                    </small>
                                    <strong
                                      className={
                                        isBlocked
                                          ? "text-warning"
                                          : "text-danger"
                                      }
                                    >
                                      {bookTicketKey || blockTicketKey || "N/A"}
                                    </strong>
                                  </div>
                                </div>
                              )}
                              {invoiceNo && (
                                <div className="col-md-3">
                                  <div className="reference-item">
                                    <small className="text-muted d-block">
                                      Invoice Number
                                    </small>
                                    <strong>{invoiceNo}</strong>
                                  </div>
                                </div>
                              )}
                              <div className="col-md-3">
                                <div className="reference-item">
                                  <small className="text-muted d-block">
                                    Status
                                  </small>
                                  <span
                                    className={`badge ${
                                      isBlocked ? "bg-warning" : "bg-danger"
                                    }`}
                                  >
                                    {bookingStatus}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Left Column - Journey & Passenger Details */}
                    <div className="col-lg-8">
                      {/* Journey Overview Card */}
                      {bus && searchQuery && (
                        <div className="card shadow-sm mb-4">
                          <div className="card-header bg-white">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiBusLine className="text-primary" />
                              Journey Overview
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="row align-items-center mb-4">
                              <div className="col-md-5 text-center">
                                <div className="boarding-point-card">
                                  <h6 className="text-primary mb-2">
                                    {searchQuery.fromStation}
                                  </h6>
                                  <div className="time-display">
                                    {formatTime(bus.departureTime)}
                                  </div>
                                  <small className="text-muted">
                                    {selectedBoardingPoint?.location}
                                  </small>
                                </div>
                              </div>
                              <div className="col-md-2 text-center">
                                <RiArrowLeftRightLine className="text-muted fs-3 mb-2" />
                                <div className="duration-badge">
                                  {bus.duration || "Direct"}
                                </div>
                              </div>
                              <div className="col-md-5 text-center">
                                <div className="boarding-point-card">
                                  <h6 className="text-success mb-2">
                                    {searchQuery.toStation}
                                  </h6>
                                  <div className="time-display">
                                    {formatTime(bus.arrivalTime)}
                                  </div>
                                  <small className="text-muted">
                                    {selectedDroppingPoint?.location}
                                  </small>
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                <div className="info-item">
                                  <RiCalendarLine className="text-muted me-2" />
                                  <div>
                                    <small className="text-muted">
                                      Travel Date
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {formatDate(searchQuery.travelDate)}
                                    </p>
                                  </div>
                                </div>
                                <div className="info-item">
                                  <RiUserLine className="text-muted me-2" />
                                  <div>
                                    <small className="text-muted">
                                      Passengers
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {passengerCount || 0} Passenger(s)
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="info-item">
                                  <RiFileList3Line className="text-muted me-2" />
                                  <div>
                                    <small className="text-muted">
                                      Bus Operator
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {bus.operatorName}
                                    </p>
                                  </div>
                                </div>
                                <div className="info-item">
                                  <RiBusLine className="text-muted me-2" />
                                  <div>
                                    <small className="text-muted">
                                      Bus Type
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {bus.busType || "Standard"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Boarding & Dropping Points */}
                      {(selectedBoardingPoint || selectedDroppingPoint) && (
                        <div className="card shadow-sm mb-4">
                          <div className="card-header bg-white">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiMapPinLine className="text-primary" />
                              Pickup & Drop Points
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {selectedBoardingPoint && (
                                <div className="col-md-6">
                                  <div className="point-details">
                                    <h6 className="text-primary mb-2">
                                      Boarding Point
                                    </h6>
                                    <p className="fw-semibold mb-1">
                                      {selectedBoardingPoint.location}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <RiTimeLine className="me-1" />
                                      {formatTime(selectedBoardingPoint.time)}
                                    </p>
                                    <p className="text-muted small mb-0">
                                      {selectedBoardingPoint.address ||
                                        selectedBoardingPoint.landmark ||
                                        selectedBoardingPoint.pointName}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {selectedDroppingPoint && (
                                <div className="col-md-6">
                                  <div className="point-details">
                                    <h6 className="text-success mb-2">
                                      Dropping Point
                                    </h6>
                                    <p className="fw-semibold mb-1">
                                      {selectedDroppingPoint.location}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <RiTimeLine className="me-1" />
                                      {formatTime(selectedDroppingPoint.time)}
                                    </p>
                                    <p className="text-muted small mb-0">
                                      {selectedDroppingPoint.address ||
                                        selectedDroppingPoint.landmark ||
                                        selectedDroppingPoint.pointName}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Passenger Details */}
                      {selectedSeats &&
                        passengerDetails &&
                        passengerDetails.length > 0 && (
                          <div className="card shadow-sm">
                            <div className="card-header bg-white">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <RiUserLine className="text-success" />
                                Passenger Details
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Seat No</th>
                                      <th>Passenger Name</th>
                                      <th>Age</th>
                                      <th>ID Proof</th>
                                      <th>Bus Fare</th>
                                      <th>Conv. Fee</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedSeats.map((seat, index) => {
                                      const seatBaseFare =
                                        seat.totalPriceWithTaxes || 0;
                                      const seatConvFee =
                                        seat.convenienceFee || 100;
                                      const seatTotal =
                                        seatBaseFare + seatConvFee;
                                      const passenger =
                                        passengerDetails[index] ||
                                        passengerDetails[0];

                                      return (
                                        <tr key={seat.id}>
                                          <td>
                                            <span className="seat-badge">
                                              {seat.id}
                                            </span>
                                          </td>
                                          <td>
                                            <strong>
                                              {passenger?.title || "Mr/Ms"}{" "}
                                              {passenger?.firstName}{" "}
                                              {passenger?.lastName}
                                            </strong>
                                            <br />
                                            <small className="text-muted">
                                              {passenger?.email || "N/A"}
                                            </small>
                                          </td>
                                          <td>
                                            {passenger?.age || "N/A"} years
                                          </td>
                                          <td>
                                            <div>
                                              <small className="text-muted d-block">
                                                {passenger?.idProof || "N/A"}
                                              </small>
                                              {passenger?.idProofNumber && (
                                                <small className="text-muted d-block">
                                                  ID:{" "}
                                                  {passenger.idProofNumber.slice(
                                                    0,
                                                    8
                                                  )}
                                                  ...
                                                </small>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            {formatCurrency(seatBaseFare)}
                                          </td>
                                          <td>{formatCurrency(seatConvFee)}</td>
                                          <td>
                                            <strong>
                                              {formatCurrency(seatTotal)}
                                            </strong>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Right Column - Summary & Actions */}
                    <div className="col-lg-4">
                      {/* Payment Action Card - Only show if blocked */}
                      {isBlocked && (
                        <div className="card shadow-sm mb-4 border-success">
                          <div className="card-header bg-success text-white">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiLockLine />
                              Complete Payment
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="text-center mb-4">
                              <div className="timer-display mb-3">
                                <RiTimerLine className="text-danger fs-1" />
                                <div className="timer-text">
                                  <div className="timer-minutes">
                                    {minutesRemaining}
                                  </div>
                                  <small className="text-muted">
                                    minutes remaining
                                  </small>
                                </div>
                              </div>
                              <p className="text-muted small">
                                Complete payment before {expiryTime}
                              </p>
                            </div>

                            <button
                              className="btn btn-success w-100 btn-lg mb-3"
                              onClick={handleCompletePayment}
                            >
                              <RiCheckboxCircleLine className="me-2" />
                              Pay {formatCurrency(totalAmount || 0)}
                            </button>

                            <div className="alert alert-info small">
                              <RiInformationLine className="me-2" />
                              Your seats will be confirmed immediately after
                              payment
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Retry Blocking Card - Only show if failed */}
                      {hasError && (
                        <div className="card shadow-sm mb-4 border-danger">
                          <div className="card-header bg-danger text-white">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiRefreshLine />
                              Try Blocking Again
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="text-center mb-4">
                              <div className="error-icon mb-3">
                                <RiCloseCircleLine className="text-danger fs-1" />
                              </div>
                              <p className="text-muted">
                                There was an issue blocking your seats. Please
                                try again.
                              </p>
                            </div>

                            <button
                              className="btn btn-primary w-100 btn-lg mb-3"
                              onClick={handleRetryBlocking}
                            >
                              <RiRefreshLine className="me-2" />
                              Try Blocking Again
                            </button>

                            <button
                              className="btn btn-outline-primary w-100 mb-2"
                              onClick={() =>
                                navigate("/bus/payment", {
                                  state: {
                                    ...location.state,
                                    bookingType: "BOOK_DIRECTLY",
                                  },
                                })
                              }
                            >
                              <RiArrowRightLine className="me-2" />
                              Book Directly Instead
                            </button>

                            <div className="alert alert-warning small">
                              <RiAlertLine className="me-2" />
                              If issue persists, please contact support
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Fare Summary */}
                      {totalAmount && (
                        <div className="card shadow-sm mb-4">
                          <div className="card-header bg-white">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiMoneyDollarCircleLine className="text-info" />
                              Fare Summary
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="fare-summary">
                              <div className="fare-item">
                                <div className="fare-label">
                                  <span>Bus Fare</span>
                                  <small className="text-muted d-block">
                                    (Includes all taxes)
                                  </small>
                                </div>
                                <div className="fare-amount">
                                  {formatCurrency(baseFare)}
                                </div>
                              </div>

                              <div className="fare-item">
                                <div className="fare-label">
                                  <span>Convenience Fee</span>
                                  <small className="text-muted d-block">
                                    (₹100 × {passengerCount || 1})
                                  </small>
                                </div>
                                <div className="fare-amount">
                                  {formatCurrency(convenienceFee)}
                                </div>
                              </div>

                              <div className="fare-divider"></div>

                              <div className="fare-item total">
                                <div className="fare-label">
                                  <strong>Total Amount</strong>
                                </div>
                                <div className="fare-amount">
                                  <strong className="text-primary fs-5">
                                    {formatCurrency(totalAmount)}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style jsx>{`
        .status-icon-container {
          width: 70px;
          height: 70px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .time-warning {
          padding: 10px;
          background: #fff3cd;
          border-radius: 8px;
          border-left: 4px solid #ffc107;
        }

        .error-details {
          padding: 10px;
          background: #f8d7da;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
        }

        .reference-item {
          padding: 10px;
          background: #f8f9fa;
          border-radius: 8px;
          text-align: center;
        }

        .boarding-point-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          border: 1px solid #e9ecef;
        }

        .time-display {
          font-size: 24px;
          font-weight: 600;
          margin: 10px 0;
        }

        .duration-badge {
          display: inline-block;
          padding: 4px 12px;
          background: #4361ee;
          color: white;
          border-radius: 20px;
          font-size: 12px;
        }

        .info-item {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }

        .point-details {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          height: 100%;
        }

        .seat-badge {
          display: inline-block;
          padding: 6px 12px;
          background: #4361ee;
          color: white;
          border-radius: 6px;
          font-weight: 600;
        }

        .timer-display {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
        }

        .timer-text {
          text-align: center;
        }

        .timer-minutes {
          font-size: 36px;
          font-weight: 700;
          color: #dc3545;
          line-height: 1;
        }

        .error-icon {
          text-align: center;
        }

        .fare-summary {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 10px;
        }

        .fare-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .fare-item.total {
          border-top: 2px solid #dee2e6;
          padding-top: 12px;
          margin-top: 12px;
        }

        .fare-divider {
          border-top: 1px dashed #dee2e6;
          margin: 15px 0;
        }

        .info-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .info-list li {
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
        }

        .info-list li:last-child {
          border-bottom: none;
        }

        @media print {
          .btn,
          .d-print-none {
            display: none !important;
          }
          .card {
            border: 1px solid #dee2e6 !important;
            page-break-inside: avoid;
          }
          .shadow-sm {
            box-shadow: none !important;
          }
          .alert {
            border: 1px solid #dee2e6 !important;
          }
        }

        @media (max-width: 768px) {
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 20px;
          }

          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }

          .status-icon-container {
            width: 50px;
            height: 50px;
          }

          .timer-display {
            flex-direction: column;
            gap: 10px;
          }

          .timer-minutes {
            font-size: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default BlockedSeatsConfirmation;
